import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, homeFor } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Facilities from "@/pages/Facilities";
import Checklists from "@/pages/Checklists";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import MyTasks from "@/pages/MyTasks";
import Reviews from "@/pages/Reviews";
import Reports from "@/pages/Reports";
import Audit from "@/pages/Audit";
import Tenants from "@/pages/Tenants";
import Notifications from "@/pages/Notifications";
import People from "@/pages/People";
import MyShift from "@/pages/MyShift";
import Billing from "@/pages/Billing";
import Profile from "@/pages/Profile";
import BillingGate from "@/components/BillingGate";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return user ? <Navigate to={homeFor(user)} replace /> : <Navigate to="/login" replace />;
}

function AppHome() {
  // /app itself — show the dashboard, but employees and supervisors are
  // redirected to their landing pages for a more useful first screen.
  const { user } = useAuth();
  if (user?.role === "employee") return <Navigate to="/app/my-tasks" replace />;
  if (user?.role === "supervisor") return <Navigate to="/app/reviews" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<AppHome />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/:id" element={<TaskDetail />} />
              <Route path="my-tasks" element={<Protected roles={["employee","supervisor","company_admin","platform_admin"]}><MyTasks /></Protected>} />
              <Route path="my-shift" element={<Protected roles={["employee","supervisor","company_admin","platform_admin"]}><MyShift /></Protected>} />
              <Route path="reviews" element={<Protected roles={["supervisor","company_admin","platform_admin"]}><Reviews /></Protected>} />
              <Route path="facilities" element={<Protected roles={["company_admin"]}><Facilities /></Protected>} />
              <Route path="people" element={<Protected roles={["company_admin","supervisor"]}><People /></Protected>} />
              <Route path="employees" element={<Navigate to="/app/people" replace />} />
              <Route path="staff" element={<Navigate to="/app/people" replace />} />
              <Route path="checklists" element={<Protected roles={["company_admin"]}><Checklists /></Protected>} />
              <Route path="billing" element={<Protected roles={["company_admin"]}><BillingGate><Billing /></BillingGate></Protected>} />
              <Route path="reports" element={<Protected roles={["supervisor","company_admin","platform_admin"]}><Reports /></Protected>} />
              <Route path="audit" element={<Protected roles={["supervisor","company_admin","platform_admin"]}><Audit /></Protected>} />
              <Route path="tenants" element={<Protected roles={["platform_admin"]}><Tenants /></Protected>} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="profile" element={<Profile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
